import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Upload, Download, FileText, X, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { companyService } from '@/services/companyService';
import { createSkuSet } from '@/lib/skuUtils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { decodeHtmlEntities } from '@/lib/utils';

const PRODUCT_CATEGORY: CategoryKey = 'product';

interface ImportError { row: number; field: string; message: string; }
interface ImportPreviewData { [key: string]: any; }

// 此頁改為「產品成本」匯入
const ProductPendingImport = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewData[]>([]);
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);
  const [companySkuSet, setCompanySkuSet] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    (async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) {
          setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
          // 預先載入當前公司的 SKU 清單以驗證
          try {
            const { supabase } = await import('@/integrations/supabase/client');
            const companyIdNum = Number(company.id);
            const { data, error } = await supabase
              .from('products')
              .select('sku_id')
              .eq('company_id', companyIdNum);
            if (error) {
              const anyRes = await supabase.from('products').select('sku_id');
              const skuList = (anyRes.data || []).map((r: any) => r?.sku_id).filter(Boolean);
              setCompanySkuSet(createSkuSet(skuList));
            } else {
              const skuList = (data || []).map((r: any) => r?.sku_id).filter(Boolean);
              setCompanySkuSet(createSkuSet(skuList));
            }
          } catch {}
        }
      } catch {}
    })();
  }, []);

  const toNullable = (v: any) => (typeof v === 'string' ? (v.trim() === '' ? null : v.trim()) : v);
  const toNumberOrNull = (v: any) => {
    const t = typeof v === 'string' ? v.trim() : v;
    if (t === '' || t === null || t === undefined) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  // 嚴格 SKU：保留前導零與原大小寫，不進行任何正規化
  const toSkuStringOrNull = (v: any) => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    return s === '' ? null : s;
  };

  // Security: Sanitize text to prevent XSS and Excel Formula Injection
  const sanitizeText = (text: string): string => {
    if (!text || typeof text !== 'string') return text;

    let sanitized = text.trim();

    // HTML escape to prevent XSS while preserving content (e.g., "<此日期前最佳：2026.12>" becomes "&lt;此日期前最佳：2026.12&gt;")
    // Escape order matters: & must be escaped first to avoid double-escaping
    sanitized = sanitized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    // Prevent Excel Formula Injection by prefixing dangerous formulas with '
    if (/^[=+\-@]/.test(sanitized)) {
      sanitized = "'" + sanitized;
    }

    // Additional security: Limit text length to prevent DoS
    if (sanitized.length > 5000) {
      sanitized = sanitized.substring(0, 5000) + '...';
    }

    return sanitized;
  };

  // Security: Validate numeric fields with range checks
  const validateNumericField = (value: any, fieldName: string, min: number = 0): { isValid: boolean; error?: string } => {
    const num = toNumberOrNull(value);
    if (num === null) return { isValid: true }; // Allow null values

    if (!Number.isFinite(num)) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}必須是有效的數字` : `${fieldName} must be a valid number` };
    }

    if (num < min) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}不能小於 ${min}` : `${fieldName} cannot be less than ${min}` };
    }

    // Additional check for extremely large numbers (potential DoS)
    if (num > 999999999) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}數值過大，請檢查數據` : `${fieldName} value too large, please check data` };
    }

    return { isValid: true };
  };

  const allColumns = ['sku_id', 'average_cost'];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Security: File size limit to prevent DoS attacks
      const MAX_SIZE = 5 * 1024 * 1024; // 5MB limit
      if (file.size > MAX_SIZE) {
        toast.error(language === 'zh' ? '檔案太大！請上傳 5MB 以下的檔案。' : 'File too large! Please upload files under 5MB.');
        if (event.target) {
          (event.target as HTMLInputElement).value = '';
        }
        return;
      }
      setSelectedFile(file);
      parseFile(file);
    }
  };

  const parseFile = (file: File) => {
    setIsProcessing(true);
    setProgress({ done: 0, total: 0 });
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsedData: ImportPreviewData[] = [];
        const validationErrors: ImportError[] = [];
        const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
        let headers: string[] = [];
        let dataRows: string[][] = [];
        if (isExcel) {
          const wb = XLSX.read(new Uint8Array(e.target?.result as ArrayBuffer), { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json<any>(sheet, { header: 1, defval: '' }) as any[];
          if (rows.length < 2) throw new Error('檔案至少需一行標題與一行資料');
          headers = (rows[0] as any[]).map((v) => String(v));
          dataRows = rows.slice(1) as any[];
        } else {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((r) => r.trim());
          if (lines.length < 2) throw new Error('檔案至少需一行標題與一行資料');
          headers = lines[0].split(',').map((h) => h.trim().replace(/"/g, ''));
          dataRows = lines.slice(1).map((line) => line.split(',').map((v) => v.trim().replace(/"/g, '')));
        }
        const normalized = (s: string) => s?.toString().trim().toLowerCase();
        const headerMap: Record<string, string> = {
          // 僅允許以下對應
          '內部編號': 'sku_id',
          '成本': 'average_cost',
          'cost': 'average_cost',
        };
        const mappedKeys: (string | null)[] = headers.map((h) => {
          const norm = normalized(h);
          return headerMap[norm] || null;
        });
        const hasAllRequired = ['sku_id', 'average_cost'].every((col) => mappedKeys.includes(col));
        if (!hasAllRequired) {
          const msg = language === 'zh' ? '找不到必要欄位（需包含：內部編號、成本）' : 'Missing required columns (need: Internal Code, Cost)';
          throw new Error(msg);
        }
        dataRows.forEach((cols, rowIdx) => {
          if (!cols || cols.length === 0) return;
          const obj: ImportPreviewData = {};
          mappedKeys.forEach((dbKey, ci) => { if (dbKey) obj[dbKey] = cols[ci] ?? ''; });
          // Security: Apply data sanitization
          obj.sku_id = sanitizeText(toSkuStringOrNull(obj.sku_id));
          obj.average_cost = toNumberOrNull(obj.average_cost);

          // Security: Enhanced numeric validation with range checks
          const costValidation = validateNumericField(obj.average_cost, language === 'zh' ? '平均成本' : 'Average Cost');
          if (!costValidation.isValid) {
            validationErrors.push({ row: rowIdx + 2, field: 'average_cost', message: costValidation.error! });
          }

          // 只嚴格要求 SKU 存在；成本若非數字則記為 null 允許匯入
          if (!obj.sku_id) validationErrors.push({ row: rowIdx + 2, field: 'sku_id', message: language === 'zh' ? '缺少 SKU（內部編號）' : 'Missing SKU ID' });
          // 允許不在公司產品清單中的 SKU；不視為錯誤
          // 內部生成鍵，但不再顯示於 UI
          if (currentCompany?.id && obj.sku_id) obj._internal_company_sku_key = `${currentCompany.id}-${obj.sku_id}`;
          parsedData.push(obj);
        });
        setPreviewData(parsedData);
        setErrors(validationErrors);
        toast.success(language === 'zh' ? 'Excel 已準備，可隨時按下「確定滙入」' : 'Excel is ready. You can click "Confirm Import" anytime');
      } catch (err: any) {
        toast.error('檔案解析失敗: ' + err.message);
      } finally {
        setIsProcessing(false);
      }
    };
    if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) reader.readAsArrayBuffer(file);
    else reader.readAsText(file);
  };

  const downloadTemplate = () => {
    // 創建 Excel 工作表
    const ws = XLSX.utils.aoa_to_sheet([
      ['內部編號', '成本'], // 第一行列名設為純文字格式
      ['SKU001', 12.34]
    ]);

    // 設定第一行（標題行）為純文字格式，確保 SKU 前導零不會被自動轉換
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:B2');
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (ws[cellAddress]) {
        ws[cellAddress].t = 'str'; // 強制設為文字類型
      }
    }

    // 創建工作簿
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '成本資料');

    // 生成 Excel 檔案
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'product_cost_template.xlsx');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('模板檔案已下載');
  };

  const exportProductCosts = async () => {
    if (!currentCompany?.id) {
      toast.error(language === 'zh' ? '未能取得公司資訊，請稍後重試' : 'Company not ready');
      return;
    }

    try {
      // 顯示載入提示
      toast.info(language === 'zh' ? '正在匯出資料，請稍候...' : 'Exporting data, please wait...');
      const { supabase } = await import('@/integrations/supabase/client');
      const companyIdNum = Number(currentCompany.id);

      // 使用分頁查詢來取得所有該公司的產品
      const PAGE_SIZE = 1000;
      let allProducts: any[] = [];
      let hasMore = true;
      let offset = 0;

      while (hasMore) {
        const { data, error } = await supabase
          .from('products')
          .select('sku_id, sku_name, sku_name_chi, sku_status, packing_spec_eng, packing_spec_chi')
          .eq('company_id', companyIdNum)
          .order('sku_id')
          .range(offset, offset + PAGE_SIZE - 1);

        if (error) {
          console.error('Export error:', error);
          toast.error(language === 'zh' ? '匯出失敗：無法取得資料' : 'Export failed: Cannot fetch data');
          return;
        }

        if (data && data.length > 0) {
          allProducts = allProducts.concat(data);
          offset += PAGE_SIZE;

          // 如果返回的資料少於 PAGE_SIZE，說明已經是最後一頁
          if (data.length < PAGE_SIZE) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      if (allProducts.length === 0) {
        toast.info(language === 'zh' ? '沒有產品可滙出' : 'No products to export');
        return;
      }

      // 取得所有產品的成本資料（批次查詢）
      const skuIds = allProducts.map(p => p.sku_id).filter(Boolean);
      let costMap = new Map<string, number | null>();

      if (skuIds.length > 0) {
        // 分批查詢成本（避免單次查詢過大）
        const COST_BATCH_SIZE = 1000;
        for (let i = 0; i < skuIds.length; i += COST_BATCH_SIZE) {
          const batch = skuIds.slice(i, i + COST_BATCH_SIZE);
          const { data: costData, error: costError } = await supabase
            .from('product_costs')
            .select('sku_id, average_cost')
            .eq('company_id', companyIdNum)
            .in('sku_id', batch);

          if (!costError && costData) {
            costData.forEach((row: any) => {
              if (row.sku_id) {
                costMap.set(String(row.sku_id), row.average_cost ?? null);
              }
            });
          }
        }
      }

      // 準備 Excel 資料（7 欄）
      const headers = language === 'zh'
        ? ['SKU Code', 'Cost', 'Product Name', 'Product Name Chi', 'Status', 'Packing Spec', 'Packing Spec Chi']
        : ['SKU Code', 'Cost', 'Product Name', 'Product Name Chi', 'Status', 'Packing Spec', 'Packing Spec Chi'];

      const exportData = allProducts.map(product => {
        const skuId = String(product.sku_id || '');
        const cost = costMap.get(skuId);
        
        return {
          'SKU Code': skuId,
          'Cost': cost !== undefined && cost !== null ? cost : '', // 無成本時留空白
          'Product Name': decodeHtmlEntities(product.sku_name || ''),
          'Product Name Chi': decodeHtmlEntities(product.sku_name_chi || ''),
          'Status': product.sku_status || '',
          'Packing Spec': decodeHtmlEntities(product.packing_spec_eng || ''),
          'Packing Spec Chi': decodeHtmlEntities(product.packing_spec_chi || '')
        };
      });

      // 創建 Excel 工作表
      const ws = XLSX.utils.json_to_sheet(exportData, {
        header: headers
      });

      // 設定第一行（標題行）為純文字格式
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:G1');
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (ws[cellAddress]) {
          ws[cellAddress].t = 'str'; // 強制設為文字類型
        }
      }

      // 設定所有 SKU Code 欄位（A 欄）為文字格式以保留前導零
      for (let row = 1; row <= exportData.length; row++) {
        const skuCellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
        if (ws[skuCellAddress]) {
          ws[skuCellAddress].t = 'str'; // 確保 SKU ID 是純文字
        }
      }

      // 設定欄寬
      ws['!cols'] = [
        { wch: 16 }, // SKU Code
        { wch: 12 }, // Cost
        { wch: 30 }, // Product Name
        { wch: 30 }, // Product Name Chi
        { wch: 12 }, // Status
        { wch: 20 }, // Packing Spec
        { wch: 20 }  // Packing Spec Chi
      ];

      // 創建工作簿
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, language === 'zh' ? '產品成本' : 'Product Costs');

      // 生成 Excel 檔案
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `product_costs_${currentCompany.code || currentCompany.name || 'company'}_${new Date().toISOString().split('T')[0]}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(language === 'zh' ? `成功匯出 ${allProducts.length} 筆產品資料` : `Successfully exported ${allProducts.length} product records`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'zh' ? '匯出失敗' : 'Export failed');
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', PRODUCT_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回主控台' : 'Back to Dashboard'}
          </Button>
          <div className="flex items-center gap-2">
            <Upload className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{language === 'zh' ? '滙入功能（產品成本）' : 'Import Product (Cost)'}
            </h1>
          </div>
          <Button onClick={exportProductCosts} variant="outline" className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50 ml-auto">
            <FileDown className="w-4 h-4 mr-2" />
            {language === 'zh' ? '匯出成本' : 'Export Costs'}
          </Button>
        </div>
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">{language === 'zh' ? '檔案上傳' : 'File Upload'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="text-foreground font-medium">{language === 'zh' ? '下載匯入模板' : 'Download Template'}</p>
                  <p className="text-muted-foreground text-sm">{language === 'zh' ? '使用我們的Excel模板確保資料格式正確' : 'Use our Excel template to ensure correct format'}</p>
                </div>
              </div>
              <Button onClick={downloadTemplate} variant="outline" className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50">
                <Download className="w-4 h-4 mr-2" />
                {language === 'zh' ? '下載模板' : 'Download'}
              </Button>
            </div>
            <div className="flex items-center gap-4">
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileSelect} className="hidden" id="cost-import-file" title={language === 'zh' ? '選擇匯入檔案（Excel 或 CSV）' : 'Choose import file (Excel or CSV)'} />
              <Button onClick={() => fileInputRef.current?.click()} className="bg-purple-600 hover:bg-purple-700 text-white" disabled={isProcessing}>
                <Upload className="w-4 h-4 mr-2" />
                {language === 'zh' ? '選擇檔案' : 'Choose File'}
              </Button>
              {selectedFile && (
                <div className="flex items-center gap-2">
                  <span className="text-foreground">{selectedFile.name}</span>
                  <Badge variant="outline" className="text-muted-foreground border-border">{(selectedFile.size / 1024).toFixed(1)} KB</Badge>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedFile(null); setPreviewData([]); }} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></Button>
                </div>
              )}
            </div>
            {isProcessing && (
              <div className="mt-3 flex items-center gap-3 text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                <span>{language === 'zh' ? '處理中...' : 'Processing...'} {progress.done} / {progress.total}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {previewData.length > 0 && (
          <Card className="bg-card/80 border-border">
            <CardHeader>
              <CardTitle className="text-foreground">{language === 'zh' ? '資料預覽 (前 10 筆)' : 'Preview (first 10 rows)'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">SKU ID</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '成本' : 'Cost'}</TableHead>
                      {/* Company + SKU 欄位不再顯示 */}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.slice(0, 10).map((row, index) => (
                      <TableRow key={index} className="border-border hover:bg-accent/50">
                        <TableCell className="text-foreground text-sm">{String(row.sku_id || '')}</TableCell>
                        <TableCell className="text-foreground text-sm">{String(row.average_cost ?? '')}</TableCell>
                        {/* 不顯示 company_sku_key */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-4">
                <Button onClick={async () => {
                  if (!currentCompany?.id) {
                    toast.error(language === 'zh' ? '未能取得公司資訊，請稍後重試' : 'Company not ready');
                    return;
                  }
                  if (previewData.length === 0) {
                    toast.error(language === 'zh' ? '請先選擇檔案' : 'Please select a file first');
                    return;
                  }
                  if (errors.length > 0) {
                    toast.error(language === 'zh' ? '檔案仍有錯誤，請修正後再匯入' : 'There are validation errors. Please fix them before importing');
                    return;
                  }
                  try {
                    setIsProcessing(true);
                    const { supabase } = await import('@/integrations/supabase/client');
                    let success = 0;
                    let failCount = 0;
                    setProgress({ done: 0, total: previewData.length });
                    const companyIdNum = Number(currentCompany.id);

                    // Security: Secure Batch Import - Process in chunks of 50 for better performance and error handling
                    const BATCH_SIZE = 50;

                    // Step 1: Prepare data for batch processing
                    const validRows: Array<{ rowIndex: number; payload: Record<string, any> }> = [];
                    for (let i = 0; i < previewData.length; i++) {
                      const row = previewData[i];
                      const baseData = {
                        sku_id: row.sku_id as string,
                        average_cost: row.average_cost ?? null,
                        average_cost_date: new Date().toISOString(),
                      };
                      validRows.push({ rowIndex: i, payload: baseData });
                    }

                    // Step 2: Process in batches of 50
                    const totalBatches = Math.ceil(validRows.length / BATCH_SIZE);
                    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
                      const startIdx = batchIndex * BATCH_SIZE;
                      const endIdx = Math.min(startIdx + BATCH_SIZE, validRows.length);
                      const batchRows = validRows.slice(startIdx, endIdx);

                      try {
                        // Process each row in the batch
                        for (const rowData of batchRows) {
                          const baseData = rowData.payload;

                          // 確保 products 有對應 SKU（避免 product_costs 外鍵錯誤）
                          const { data: prod, error: prodErr } = await supabase
                            .from('products')
                            .select('sku_id')
                            .eq('sku_id', baseData.sku_id)
                            .maybeSingle();

                          if (!prod && !prodErr) {
                            const { error: insProdErr } = await supabase
                              .from('products')
                              .insert([{ sku_id: baseData.sku_id }]);
                            if (insProdErr) {
                              failCount++;
                              continue;
                            }
                          } else if (prodErr && prodErr.code !== 'PGRST116') {
                            failCount++;
                            continue;
                          }

                          // 先查是否存在（優先 company_id + sku_id，失敗再退回 sku_id）
                          let found: any = null;
                          let selErr: any = null;
                          const q1 = await supabase
                            .from('product_costs')
                            .select('id')
                            .eq('company_id', companyIdNum)
                            .eq('sku_id', baseData.sku_id)
                            .maybeSingle();

                          if (q1.error) {
                            selErr = q1.error;
                            const q2 = await supabase
                              .from('product_costs')
                              .select('id')
                              .eq('sku_id', baseData.sku_id)
                              .maybeSingle();
                            if (!q2.error) {
                              found = q2.data;
                              selErr = null;
                            }
                          } else {
                            found = q1.data;
                          }

                          if (selErr && selErr.code !== 'PGRST116') {
                            failCount++;
                            continue;
                          }

                          if (found) {
                            const { error: updErr } = await supabase
                              .from('product_costs')
                              .update({
                                average_cost: baseData.average_cost,
                                average_cost_date: baseData.average_cost_date
                              })
                              .eq('id', (found as any).id);
                            if (updErr) {
                              failCount++;
                            } else {
                              success++;
                            }
                          } else {
                            // 插入新記錄
                            const insertData = { company_id: companyIdNum, ...baseData } as Record<string, any>;
                            const { error: insErr } = await supabase.from('product_costs').insert([insertData]);
                            if (insErr) {
                              failCount++;
                            } else {
                              success++;
                            }
                          }
                        }

                        // Update progress after each batch
                        const processedRows = Math.min((batchIndex + 1) * BATCH_SIZE, validRows.length);
                        setProgress({ done: processedRows + (previewData.length - validRows.length), total: previewData.length });

                      } catch (err: any) {
                        // Batch failed - mark all remaining rows in this batch as failed
                        failCount += (endIdx - startIdx);
                        console.warn('Batch cost import warning:', err?.message || err);
                      }
                    }
                    if (failCount === 0) {
                      toast.success(language === 'zh' ? `匯入完成：成功 ${success} 筆` : `Import complete: ${success} rows successful`);
                    } else {
                      toast.warning(
                        language === 'zh'
                          ? `匯入完成：成功 ${success} 筆，失敗 ${failCount} 筆。請檢查欄位/權限/索引。`
                          : `Import complete: ${success} successful, ${failCount} failed. Check columns/permissions/indexes.`
                      );
                    }
                  } catch (e: any) {
                    console.error(e);
                    toast.error(language === 'zh' ? '匯入失敗' : 'Import failed');
                  } finally {
                    setIsProcessing(false);
                  }
                }} className="bg-green-600 hover:bg-green-700 text-white" disabled={isProcessing}>
                  {language === 'zh' ? '確認匯入' : 'Confirm Import'}
                </Button>
                {progress.total > 0 && (
                  <div className="mt-3 space-y-2">
                    <Progress value={progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100)} />
                    <div className="text-muted-foreground text-sm">{language === 'zh' ? '進度' : 'Progress'}: {progress.done} / {progress.total}</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProductPendingImport;


